import loadPreactModule from "../../../utils/loadPreactModule";
import renderPreactWithProps from "../../../utils/renderPreactWithProps";
import callIfArgumentsPass from "../../../utils/callIfArgumentsPass";
import pipe from "../../../lib/core/functional/pipe";
import curry from "../../../lib/core/functional/curry";
import noop from "../../../lib/core/functional/noop";
import raiseNotification from "../../../utils/raiseNotification";
import { getFolders, removeFromFolder, saveToFolder } from "../../../transport/folders";
import { didUserInfoChanged, getUserCommunity, canManageLabels } from "../_common";

const BUCKET_REGEX = /(community|user)\((?:\d+)\):(?:saved|shared)([a-z0-9_-]*)?/i;
const LEADING_DASH = /^-/;

function validateBucketName(bucketName) {
    return BUCKET_REGEX.test(bucketName);
}

function getBucketInfo(bucketName) {
    const [_, type, folderName] = bucketName.match(BUCKET_REGEX);
    return [type === "user", folderName ? folderName.replace(LEADING_DASH, "") : null];
}

const cachedFolders = {};

const folderWaiters = {};

const storeResult = curry(function storeResult(userId, res) {
    cachedFolders[userId] = res;
    return res;
});

const resolvePromises = curry(function resolvePromises(promiseHandlers, res) {
    promiseHandlers.forEach(([resolve]) => resolve(res));
});

const rejectPromises = curry(function rejectPromises(promiseHandlers, err) {
    promiseHandlers.forEach(([_, reject]) => reject(err));
});

function getSlug(label) {
    return label.slug;
}

function getSlugs(response) {
    return response ? response.labels.map(getSlug) : null;
}

function getResponsesSlugs(allResponses) {
    return allResponses.map(getSlugs);
}

function getCachedFolders(userInfo) {
    const userId = userInfo.profileSite.id;

    if (userId in cachedFolders) {
        return Promise.resolve(cachedFolders[userId]);
    }

    function promiseHandler(resolve, reject) {
        const promiseResolution = [resolve, reject];
        if (userId in folderWaiters) {
            folderWaiters[userId].push(promiseResolution);
            return;
        }

        folderWaiters[userId] = [promiseResolution];

        const community = getUserCommunity(userInfo);
        const canManageOwnLabels = canManageLabels(userInfo);

        const promises = [];

        if (canManageOwnLabels) {
            promises.push(getFolders(), getFolders(true));
        } else {
            promises.push(Promise.resolve(null), Promise.resolve(null));
        }

        if (community) {
            promises.push(getFolders(false, community), getFolders(true, community));
        }

        Promise.all(promises)
            .then(getResponsesSlugs)
            .then(storeResult(userId))
            .then(resolvePromises(folderWaiters[userId]))
            .catch(rejectPromises(folderWaiters[userId]));
    }

    return new Promise(promiseHandler);
}

export const renderFoldersPostManager = curry(function renderFoldersButton(
    renderModule,
    isUserFolder,
    folderSlug,
    postId,
    getFolders,
    removeFromFolder,
    saveToFolder,
    err,
    userInfo
) {
    let ignore = false;

    let isInFolder = null;
    let onClick = noop;
    let success = null;
    let disabled = false;
    let isLoggedIn = !err;

    function updateRender() {
        if (ignore) {
            return;
        }
        renderModule({
            isLoggedIn,
            disabled,
            success,
            isInFolder,
            onClick,
        });
    }

    if (err) {
        isInFolder = false;
        updateRender();
        return;
    }

    const community = getUserCommunity(userInfo);

    function removePostFromFolder() {
        return removeFromFolder(isUserFolder ? null : community, postId, [folderSlug]);
    }

    function savePostToFolder() {
        return saveToFolder(isUserFolder ? null : community, postId, [folderSlug]);
    }

    function resetSuccess() {
        success = null;
    }

    function disableButton() {
        disabled = true;
    }

    function enableButton() {
        disabled = false;
    }

    const enableAndReset = pipe(enableButton, updateRender);

    const clickToRemove = pipe(disableButton, resetSuccess, updateRender, function clickToRemove() {
        removePostFromFolder().then(onDeleteSuccess).catch(renderError).finally(enableAndReset);
    });

    const clickToAdd = pipe(disableButton, resetSuccess, updateRender, function clickToAdd() {
        savePostToFolder().then(onAddSuccess).catch(renderError).finally(enableAndReset);
    });

    function onAddSuccess() {
        success = true;
        isInFolder = true;
        onClick = clickToRemove;
    }

    function onDeleteSuccess() {
        success = true;
        isInFolder = false;
        onClick = clickToAdd;
    }

    function renderError() {
        success = false;
    }

    function checkIfFolderExists([
        userGlobalFolders,
        userCustomFolders,
        communityGlobalFolders,
        communityCustomFolders,
    ]) {
        const [globalFolders, customFolders] = isUserFolder
            ? [userGlobalFolders, userCustomFolders]
            : [communityGlobalFolders, communityCustomFolders];

        const found =
            (globalFolders && globalFolders.indexOf(folderSlug) >= 0) ||
            (customFolders && customFolders.indexOf(folderSlug) >= 0);

        if (found) {
            onClick = clickToRemove;
            isInFolder = true;
        }
    }

    getFolders(userInfo).then(checkIfFolderExists).catch(renderError).finally(updateRender);

    return function cleanup() {
        ignore = true;
    };
});

const onModule = curry(function onModule(context, postElement, [renderer, Module]) {
    const postId = postElement.getAttribute("elid");
    const postsElement = postElement.closest(".posts-custom");

    if (!postId || !postsElement) {
        return;
    }

    const { attrFrom_buckets } = postsElement.dataset;

    if (!attrFrom_buckets || !validateBucketName(attrFrom_buckets)) {
        return;
    }

    const [isUserFolder, folderSlug] = getBucketInfo(attrFrom_buckets);

    if (!folderSlug) {
        return;
    }

    const element = document.createElement("div");
    element.classList.add("rm-folders-toggle-button");
    postElement.appendChild(element);

    context.listenForUserInfo(
        callIfArgumentsPass(
            didUserInfoChanged,
            renderFoldersPostManager(
                renderPreactWithProps(renderer, Module, element),
                isUserFolder,
                folderSlug,
                postId,
                getCachedFolders,
                removeFromFolder,
                saveToFolder
            )
        )
    );
});

const internalLoader = () => import("./module").then((module) => module.default);

export default function renderer(context, element) {
    loadPreactModule("FoldersPostManager", context, { raiseNotification }, internalLoader).then(
        onModule(context, element)
    );
}
